import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import SaveToWishlistButton from '../../../componants/wishlist/SaveToWishlistButton'
import useIndividualWishlistItems, { wishlistItemKey } from '../../../componants/wishlist/useIndividualWishlistItems'
import Modal from '../../../componants/modal/Modal'
import ReviewsRatingSection from '../../../componants/reviewsRating/ReviewsRatingSection'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import { mapVenueListItem, parseVenueCapacity, pickListingPrice, VENUE_PRICING_TYPE_LABELS } from '../../../utils/venueFormat'
import {
    Age,
    Calendar,
    Cross,
    GreenTick,
    HotelSample,
    Location,
    Smallarw,
    StarWhite,
    Watch,
    Vector,
} from '../../../assests/ExportImageIcon'
import "../../hotel/hotelListing/hotelResults.css"
import '../../hotel/hotel-details/hotelDetail.css'
import '../venueShared.css'
import useAppOptions from '../../../hooks/useAppOptions'

const BOOK_ENTIRE_VENUE = {
    title: 'Book Entire Venue',
    desc: 'Request a custom proposal for your event. Our team will work with you to design the perfect setup.',
    perks: ['Flexible booking duration', 'Custom catering options', 'Dedicated event coordinator'],
}

const VenueDetail = () => {
    const navigate = useNavigate()
    const { id } = useParams()
    const [venue, setVenue] = useState(null)
    const [showFullAbout, setShowFullAbout] = useState(false)
    const [activeLandmarkTab, setActiveLandmarkTab] = useState(null)
    const [galleryOpen, setGalleryOpen] = useState(false)
    const { individualId, savedMap } = useIndividualWishlistItems()

    useEffect(() => {
        if (!id) return
        service
            .get(endpoints.venues.detail.replace('{id}', id), {}, { silent: true })
            .then(({ data }) => {
                setVenue(data?.data ?? data ?? null)
            })
            .catch(() => setVenue(null))
    }, [id])

    // Confirmed field names from GET /users/venues/{id} — this is a
    // DIFFERENT schema than the list endpoint: category_name (not "type"),
    // address_summary/address_full/distance_summary (not "address"),
    // capacity/price may literally be the string "N/A", about (not
    // "detailed_desc"), cover_image (not "cover_photo"), photo_gallery,
    // property_summary is a single object (not an array), property_rules,
    // key_landmarks/transport, and similar_properties (which reuses the
    // LIST endpoint's schema, so it goes through the same shared mapper).
    // No rating/reviews_count field exists on this endpoint at all.
    const venueName = venue?.name || ''
    // photo_gallery items are objects ({id, venue, image}), not plain URL
    // strings — extract .image before using them as <img src>.
    const galleryFromPhotos = Array.isArray(venue?.photo_gallery)
        ? venue.photo_gallery.map((item) => item.image).filter(Boolean)
        : []
    const galleryImages = galleryFromPhotos.length
        ? galleryFromPhotos
        : (venue?.cover_image ? [venue.cover_image] : [])
    const city = venue?.address_summary || ''
    // Confirmed live 2026-09-21: `distance_summary` comes back as the
    // literal string "N/A" (not computed yet) — don't show that as if it
    // were a real distance value.
    const locationNote = (venue?.distance_summary && venue.distance_summary !== 'N/A') ? venue.distance_summary : ''
    const capacityValue = parseVenueCapacity(venue?.capacity)
    const capacityDisplay = capacityValue != null ? `Up to ${capacityValue} Guests` : null
    // Confirmed live 2026-09-21: this endpoint's own top-level `price` field
    // is literally "N/A" — the real per-type prices live in `pricings[]`
    // (already used lower down for the Pricing Options section).
    const priceDisplay = venue ? pickListingPrice(venue).display : '—'
    const packageTitle = venue?.category_name || ''
    const aboutFull = venue?.about || ''
    const aboutText = aboutFull
        ? (showFullAbout ? aboutFull : `${aboutFull.slice(0, 280)}${aboutFull.length > 280 ? '…' : ''}`)
        : ''
    const amenities = Array.isArray(venue?.amenities)
        ? venue.amenities.map((item) => (typeof item === 'string' ? item : item?.name)).filter(Boolean)
        : []
    const address = (venue?.address_full || venue?.address_summary)
        ? { line: [venue.address_full, venue.address_summary].filter(Boolean).join(', '), note: locationNote }
        : null
    const propertySummary = venue?.property_summary
        ? [
            { icon: Watch, label: 'Check-in', value: venue.property_summary.check_in },
            { icon: Watch, label: 'Check-out', value: venue.property_summary.check_out },
            { icon: Cross, label: 'Pets', value: venue.property_summary.pets },
            { icon: Age, label: 'Smoking', value: venue.property_summary.smoking },
        ].filter((item) => item.value)
        : []
    // `included`/`not_included` are the pre-split flat form of `property_rules`
    // ({venue_rule: 'included'|'not_included', description}) — use these directly
    // instead of re-deriving from property_rules.
    const includedItems = Array.isArray(venue?.included) ? venue.included.filter(Boolean) : []
    const notIncludedItems = Array.isArray(venue?.not_included) ? venue.not_included.filter(Boolean) : []
    const guestRange = (venue?.min_guests != null && venue?.max_guests != null)
        ? `${venue.min_guests} - ${venue.max_guests} Guests`
        : capacityDisplay
    const areaDisplay = venue?.area != null ? `${Number(venue.area).toLocaleString('en-IN')} sq. ft.` : null
    const suitableEvents = Array.isArray(venue?.events) ? venue.events.map((item) => item?.name).filter(Boolean) : []
    // Labels from the admin-defined master list when available (GET
    // /admin-panel/app-options/list?group=venue_pricing_type), falling back to
    // the static map — same resolution order as the RFP form's pricing select.
    const pricingTypeAppOptions = useAppOptions('venue_pricing_type')
    const pricingTypeLabel = (value) => (
        pricingTypeAppOptions.find((opt) => opt.value === value)?.label
        || VENUE_PRICING_TYPE_LABELS[value]
        || value
    )
    const pricingRows = Array.isArray(venue?.pricings) ? venue.pricings : []
    const peakSeasons = Array.isArray(venue?.peak_season) ? venue.peak_season : []
    const formatPeakDate = (dateStr) => {
        const parsed = new Date(dateStr)
        return Number.isNaN(parsed.getTime())
            ? dateStr
            : parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    }
    const cancellationRows = Array.isArray(venue?.cancellation_policies)
        ? venue.cancellation_policies.map((policy, index) => {
            const unit = policy.policy_type + (policy.duration_before === 1 ? '' : 's')
            const parts = [`${policy.duration_before} ${unit} before event`]
            if (policy.refund_percentage != null) parts.push(`${policy.refund_percentage}% refund`)
            if (policy.description) parts.push(policy.description)
            return { key: policy.id ?? index, text: parts.join(' — ') }
        })
        : []
    const landmarkTabs = [
        Array.isArray(venue?.key_landmarks) && venue.key_landmarks.length ? 'Key Landmarks' : null,
        Array.isArray(venue?.transport) && venue.transport.length ? 'Transport' : null,
    ].filter(Boolean)
    const landmarksByTab = {
        'Key Landmarks': venue?.key_landmarks || [],
        'Transport': venue?.transport || [],
    }
    const currentLandmarkTab = landmarkTabs.includes(activeLandmarkTab) ? activeLandmarkTab : landmarkTabs[0]
    const similarVenues = Array.isArray(venue?.similar_properties)
        ? venue.similar_properties.map((item) => mapVenueListItem(item, HotelSample))
        : []
    const mapQuery = venue?.latitude && venue?.longitude
        ? `${venue.latitude},${venue.longitude}`
        : (city ? encodeURIComponent(city) : '')

    const handleSimilarVenueClick = (similarVenue) => {
        navigate(`/venue-detail/${similarVenue.id}`)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    return (
        <>
            <section className='allSearchArea commonTopPadding'>
                <div className='container'>
                    <div className='row g-4'>
                        <div className='col-md-12'>
                            <div className='hotelAllDetail'>
                                <div className='hotelDetailGrid'>
                                    <div className='hotelDetailLeft'>
                                        <div className='hotelGallery event__detailGallery'>
                                            {galleryImages.length ? (
                                                <>
                                                    <div className='hotelGallery__main' onClick={() => setGalleryOpen(true)} role="button" tabIndex={0} style={{ cursor: 'pointer' }}>
                                                        <img src={galleryImages[0]} alt={venueName} />
                                                        {venue && (
                                                            <SaveToWishlistButton
                                                                item={{
                                                                    recordModule: 'venue',
                                                                    recordId: venue.id,
                                                                    recordName: venueName,
                                                                    imageUrl: galleryImages[0],
                                                                }}
                                                                preSavedItemId={savedMap.get(wishlistItemKey('venue', venue.id))}
                                                                preSavedWishlistId={individualId}
                                                            />
                                                        )}
                                                    </div>
                                                    {galleryImages.length > 1 && (
                                                        <div className='hotelGallery__grid'>
                                                            {galleryImages[1] && <img src={galleryImages[1]} alt={venueName} onClick={() => setGalleryOpen(true)} role="button" tabIndex={0} style={{ cursor: 'pointer' }} />}
                                                            {galleryImages[2] && <img src={galleryImages[2]} alt={venueName} onClick={() => setGalleryOpen(true)} role="button" tabIndex={0} style={{ cursor: 'pointer' }} />}
                                                            {galleryImages[3] && <img src={galleryImages[3]} alt={venueName} onClick={() => setGalleryOpen(true)} role="button" tabIndex={0} style={{ cursor: 'pointer' }} />}
                                                            <div className='hotelGallery__lastItem'>
                                                                {galleryImages[4] && <img src={galleryImages[4]} alt={venueName} onClick={() => setGalleryOpen(true)} role="button" tabIndex={0} style={{ cursor: 'pointer' }} />}
                                                                <button type='button' className='hotelGallery__showAll' onClick={() => setGalleryOpen(true)}>
                                                                    Show all photos ({galleryImages.length})
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <div className='venueGallery__placeholder'>No photos available yet</div>
                                            )}
                                        </div>

                                        <div className='hotelInfoCard'>
                                            <div className='hotelInfoCard__top'>
                                                <div>
                                                    <div className='hotelInfoCard__name'>{venueName}</div>
                                                </div>
                                                {/* No rating/reviews_count field on GET /users/venues/{id} — nothing real to show. */}
                                            </div>

                                            {(city || locationNote) && (
                                                <>
                                                    <div className='hotelInfoCard__divider' />

                                                    <div className='hotelInfoCard__location'>
                                                        <span className='hotelInfoCard__locationIcon'>
                                                            <img src={Location} alt="" />
                                                        </span>
                                                        <div className='hotelCard__location'>
                                                            <div>
                                                                {city && <div className='hotelCard__locationName'>{city}</div>}
                                                                {locationNote && <div className='hotelCard__locationNote'>{locationNote}</div>}
                                                            </div>
                                                        </div>
                                                        <button type='button' className='hotelInfoCard__arrow' aria-label="View location">
                                                            <img src={Smallarw} alt="" />
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <div className='hotelDetailRight'>
                                        <div className='hotelRoomCard'>
                                            {packageTitle && <div className='hotelRoomCard__title'>{packageTitle}</div>}
                                            {guestRange && <div className='hotelRoomCard__fits'>{guestRange}</div>}
                                            {areaDisplay && <div className='hotelRoomCard__fits'>Area: {areaDisplay}</div>}

                                            <div className='hotelRoomCard__priceBlock'>
                                                <div className='hotelRoomCard__oldPriceRow'>
                                                    <span className='hotelRoomCard__perNight'>Starting From</span>
                                                </div>
                                                <div className='hotelRoomCard__price'>{priceDisplay}</div>
                                            </div>

                                            {venue?.has_active_proposal ? (
                                                <>
                                                    <button
                                                        type='button'
                                                        className='commonBtn w-100'
                                                        onClick={() => navigate('/venue-booking-confirm')}
                                                    >
                                                        View Proposal
                                                    </button>
                                                    <p className='bsFareNote mt-2'>
                                                        You already have an active proposal request for this venue.
                                                    </p>
                                                </>
                                            ) : (
                                                <button
                                                    type='button'
                                                    className='commonBtn w-100'
                                                    onClick={() => navigate(`/venue-booking-detail/${id}`)}
                                                >
                                                    Request Proposal
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {venue?.video_url && (
                            <div className='col-md-12'>
                                <div className='bsSectionTitle'>Venue Video</div>
                                <div className='bgWhite commonBorder borderRadius20 paddingCommon20'>
                                    <video controls className='venueDetailVideo' src={venue.video_url} poster={galleryImages[0] || undefined}>
                                        Your browser does not support the video tag.
                                    </video>
                                </div>
                            </div>
                        )}

                        {aboutText && (
                            <div className='col-md-12'>
                                <div className='bsSectionTitle'>About This Venue</div>
                                <div className='aboutProperty bgWhite commonBorder borderRadius20 paddingCommon20'>
                                    <p className='aboutProperty__desc'>
                                        {aboutText}
                                    </p>
                                    {aboutFull.length > 280 && (
                                        <button
                                            type='button'
                                            className='aboutProperty__readMore'
                                            onClick={() => setShowFullAbout((prev) => !prev)}
                                        >
                                            {showFullAbout ? 'READ LESS' : 'READ MORE'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {amenities.length > 0 && (
                            <div className='col-md-12'>
                                <div className='amenities__head'>
                                    <div className='bsSectionTitle'>Amenities</div>
                                </div>
                                <div className='amenities'>
                                    <div className='amenities__row bgWhite commonBorder borderRadius20 paddingCommon20'>
                                        {amenities.map((label, index) => (
                                            <div className='amenities__item' key={index}>
                                                <img src={GreenTick} alt="" />
                                                <span>{label}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {suitableEvents.length > 0 && (
                            <div className='col-md-12'>
                                <div className='bsSectionTitle'>Suitable For</div>
                                <div className='bgWhite commonBorder borderRadius20 paddingCommon20 venueEventChips'>
                                    {suitableEvents.map((name, index) => (
                                        <span className='venueEventChip' key={index}>{name}</span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {pricingRows.length > 0 && (
                            <div className='col-md-12'>
                                <div className='propertySummary'>
                                    <div className='bsSectionTitle'>Pricing Options</div>
                                    <div className='propertySummary__row'>
                                        {pricingRows.map((row, index) => {
                                            const uniqueSlots = Array.isArray(row.event_slots)
                                                ? Array.from(
                                                    new Map(
                                                        row.event_slots.map((slot) => [`${slot.slot_name}-${slot.start_time}-${slot.end_time}`, slot])
                                                    ).values()
                                                )
                                                : []
                                            const formatAmount = (amount) => (row.currency === 'INR' ? '₹' : `${row.currency} `) + Number(amount).toLocaleString('en-IN')
                                            return (
                                                <div className='propertySummaryCard bgWhite commonBorder borderRadius20 venuePricingCard' key={index}>
                                                    <div className='propertySummaryCard__label'>{pricingTypeLabel(row.pricing_type)}</div>
                                                    <div className='propertySummaryCard__value'>{formatAmount(row.price)}</div>
                                                    {(row.weekend_price != null || row.holiday_price != null) && (
                                                        <div className='venuePricingCard__sub'>
                                                            {row.weekend_price != null && <span>Weekend {formatAmount(row.weekend_price)}</span>}
                                                            {row.holiday_price != null && <span>Holiday {formatAmount(row.holiday_price)}</span>}
                                                        </div>
                                                    )}
                                                    {uniqueSlots.length > 0 && (
                                                        <div className='venuePricingCard__slots'>
                                                            {uniqueSlots.map((slot) => (
                                                                <span className='venueEventChip' key={slot.id}>
                                                                    {slot.slot_name} {slot.start_time}–{slot.end_time}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className='col-md-12'>
                            <div className='aboutProperty bgWhite greenBorder borderRadius20 paddingCommon20'>
                                <div className='d-flex gap-2 bsSectionTitle'> <img src={Vector} alt="" />{BOOK_ENTIRE_VENUE.title}</div>
                                <p className='aboutProperty__desc'>
                                    {BOOK_ENTIRE_VENUE.desc}
                                </p>
                                <ul className='bsInfoList mb-0'>
                                    {BOOK_ENTIRE_VENUE.perks.map((perk) => (
                                        <li key={perk}>{perk}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        {(address || mapQuery) && (
                            <div className='col-md-12'>
                                <div className='bsSectionTitle'>Location</div>
                                <div className='hotelLocation bgWhite commonBorder borderRadius20 paddingCommon20'>
                                    <div className='hotelLocation__grid'>
                                        {address && (
                                            <div className='hotelLocation__left'>
                                                <div className='hotelLocation__address'>
                                                    <strong>Address:</strong> {address.line}
                                                </div>
                                                {address.note ? <div className='hotelCard__locationNote'>{address.note}</div> : null}

                                                {landmarkTabs.length > 0 && (
                                                    <>
                                                        <div className='hotelLocation__tabs'>
                                                            {landmarkTabs.map((tab) => (
                                                                <span
                                                                    key={tab}
                                                                    className={`hotelLocation__tab ${currentLandmarkTab === tab ? 'active' : ''}`}
                                                                    onClick={() => setActiveLandmarkTab(tab)}
                                                                >
                                                                    {tab}
                                                                </span>
                                                            ))}
                                                        </div>

                                                        <ul className='hotelLocation__list'>
                                                            {landmarksByTab[currentLandmarkTab].map((item, index) => (
                                                                <li key={index}>
                                                                    <span>{item.name}</span>
                                                                    <span className='hotelLocation__listRight'>
                                                                        {item.distance}
                                                                        <img src={Smallarw} alt="" />
                                                                    </span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        {mapQuery && (
                                            <div className='hotelLocation__map'>
                                                <iframe
                                                    title='Venue Location'
                                                    src={`https://www.google.com/maps?q=${mapQuery}&z=14&output=embed`}
                                                    loading='lazy'
                                                    allowFullScreen
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {peakSeasons.length > 0 && (
                            <div className='col-md-12'>
                                <div className='amenities__head'>
                                    <div className='bsSectionTitle'>Peak Season Dates</div>
                                </div>
                                <p className='venuePeakSeasonNote'>Weekend &amp; holiday pricing applies during these periods</p>
                                <div className='amenities'>
                                    <div className='amenities__row bgWhite commonBorder borderRadius20 paddingCommon20'>
                                        {peakSeasons.map((season) => (
                                            <div className='amenities__item' key={season.id}>
                                                <img src={Calendar} alt="" />
                                                <span>{formatPeakDate(season.start_date)} – {formatPeakDate(season.end_date)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {propertySummary.length > 0 && (
                            <div className='col-md-12'>
                                <div className='propertySummary'>
                                    <div className='bsSectionTitle'>Property Summary</div>
                                    <div className='propertySummary__row'>
                                        {propertySummary.map((item) => (
                                            <div className='propertySummaryCard bgWhite commonBorder borderRadius20' key={item.label}>
                                                <span className='propertySummaryCard__icon'><img src={item.icon} alt="" /></span>
                                                <div className='propertySummaryCard__label'>{item.label}</div>
                                                <div className='propertySummaryCard__value'>{item.value}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {includedItems.length > 0 && (
                            <div className='col-md-12'>
                                <div className='bsSectionTitle'>What&apos;s Included</div>
                                <div className='bgWhite commonBorder borderRadius20 paddingCommon20'>
                                    <ul className='venueChecklist'>
                                        {includedItems.map((item, index) => (
                                            <li key={index}><img src={GreenTick} alt="" />{item}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}

                        {notIncludedItems.length > 0 && (
                            <div className='col-md-12'>
                                <div className='bsSectionTitle'>Not Included</div>
                                <div className='bgWhite commonBorder borderRadius20 paddingCommon20'>
                                    <ul className='venueChecklist venueChecklist--negative'>
                                        {notIncludedItems.map((item, index) => (
                                            <li key={index}><img src={Cross} alt="" />{item}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}

                        {cancellationRows.length > 0 && (
                            <div className='col-md-12'>
                                <div className='bsSectionTitle'>Cancellation Policy</div>
                                <div className='bgWhite commonBorder borderRadius20 paddingCommon20'>
                                    <ul className='bsInfoList mb-0'>
                                        {cancellationRows.map((row) => (
                                            <li key={row.key}>{row.text}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}

                        <div className='col-md-12'>
                            <ReviewsRatingSection service='venue' objectId={venue?.id ?? id} />
                        </div>

                        {similarVenues.length > 0 && (
                            <div className='col-md-12'>
                                <div className='similarProperty'>
                                    <div className='bsSectionTitle'>Similar Properties</div>
                                    <div className='hotelList'>
                                        {similarVenues.map((similarVenue) => (
                                            <div
                                                className='hotelListItem'
                                                key={similarVenue.id}
                                                role='button'
                                                tabIndex={0}
                                                onClick={() => handleSimilarVenueClick(similarVenue)}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                        event.preventDefault()
                                                        handleSimilarVenueClick(similarVenue)
                                                    }
                                                }}
                                            >
                                                <div className='hotelCard'>
                                                    <div className='hotelCard__media'>
                                                        <img src={similarVenue.images[0]} alt={similarVenue.name} />
                                                        <SaveToWishlistButton
                                                            item={{
                                                                recordModule: 'venue',
                                                                recordId: similarVenue.id,
                                                                recordName: similarVenue.name,
                                                                imageUrl: similarVenue.images?.[0],
                                                            }}
                                                            preSavedItemId={savedMap.get(wishlistItemKey('venue', similarVenue.id))}
                                                            preSavedWishlistId={individualId}
                                                        />
                                                    </div>

                                                    <div className='hotelCard__info'>
                                                        <div className='hotelCard__topRow'>
                                                            <div>
                                                                {similarVenue.rating != null && (
                                                                    <div className='hotelCard__rating'>
                                                                        <span className='hotelCard__ratingBadge'>
                                                                            <img src={StarWhite} alt="" /> {similarVenue.rating}
                                                                        </span>
                                                                        {similarVenue.reviewsCount != null ? (
                                                                            <span className='hotelCard__priceLabel'>({Number(similarVenue.reviewsCount).toLocaleString('en-IN')} Ratings)</span>
                                                                        ) : null}
                                                                    </div>
                                                                )}
                                                                <div className='hotelCard__name'>{similarVenue.name}</div>
                                                                {similarVenue.venueType ? <div className='hotelCard__locationNote'>{similarVenue.venueType}</div> : null}
                                                                {similarVenue.capacity ? <div className='hotelCard__locationNote'>Capacity: {similarVenue.capacity} Guests</div> : null}
                                                            </div>
                                                            <div className='hotelCard__price'>
                                                                <div className='hotelCard__priceValue'>{similarVenue.price}</div>
                                                                {similarVenue.priceUnit ? <div className='hotelCard__priceLabel'>Per {similarVenue.priceUnit}</div> : null}
                                                            </div>
                                                        </div>

                                                        <div className='hotelCard__bottomRow mt-4'>
                                                            <div className='hotelCard__location'>
                                                                <img src={Location} alt="" />
                                                                <div>
                                                                    <div className='hotelCard__locationName'>{similarVenue.location}</div>
                                                                </div>
                                                            </div>
                                                            {similarVenue.amenities.length ? (
                                                                <div className='hotelCard__amenities'>
                                                                    {similarVenue.amenities.map((amenity) => (
                                                                        <span className='hotelCard__amenityPill' key={amenity}>{amenity}</span>
                                                                    ))}
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <button type='button' className='travellerAddBtn w-100' onClick={() => navigate('/venue-list')}>
                                        View All Properties
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            <Modal open={galleryOpen} onClose={() => setGalleryOpen(false)} maxWidth={880} className="policyModal">
                <div className='venueGalleryModal'>
                    <div className='venueGalleryModal__title'>
                        {venueName} — {galleryImages.length} Photo{galleryImages.length === 1 ? '' : 's'}
                    </div>
                    <div className='venueGalleryModal__grid'>
                        {galleryImages.map((src, index) => (
                            <img key={index} src={src} alt={`${venueName} ${index + 1}`} />
                        ))}
                    </div>
                </div>
            </Modal>
        </>
    )
}

export default VenueDetail
